import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { EmailTemplateList } from "./components/EmailTemplateList";
import { EmailTemplateEditDialog } from "./components/EmailTemplateEditDialog";
import { EmailTemplate } from "./types";
import { axiosInstance } from "@/pages/auth/auth.context";
import { Loader2, Plus } from "lucide-react";

export default function ManageEmailTemplates() {
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [selectedTemplate, setSelectedTemplate] =
    useState<EmailTemplate | null>(null);
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(
        `${process.env.REACT_APP_BASE_API_URL}notificationtemplates`,
      );

      const sortedTemplates = [...response.data].sort((a, b) =>
        a.name.localeCompare(b.name),
      );

      setTemplates(sortedTemplates);
      setError(null);
    } catch (err) {
      console.error("Error fetching email templates:", err);
      setError("Failed to load email templates. Please try again.");
      toast({
        title: "Error",
        description: "Could not load email templates",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTemplates();
  }, []);

  const handleEdit = (template: EmailTemplate) => {
    setSelectedTemplate(template);
    setIsEditorOpen(true);
  };

  const handleCreate = () => {
    setSelectedTemplate({
      id: 0,
      name: "",
      subject: "",
      content: "",
      emailContent: "",
      smsContent: "",
      category: "WELCOME",
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    });
    setIsCreating(true);
    setIsEditorOpen(true);
  };

  const handleSave = async (updatedTemplate: EmailTemplate) => {
    try {
      if (isCreating) {
        const payload = {
          name: updatedTemplate.name,
          subject: updatedTemplate.subject,
          emailContent: updatedTemplate.emailContent || updatedTemplate.content,
          smsContent: updatedTemplate.smsContent || "",
        };
        await axiosInstance.post(
          `${process.env.REACT_APP_BASE_API_URL}notificationtemplates`,
          payload,
        );
        toast({
          title: "Template Created",
          description: "Email template has been successfully created.",
          variant: "success",
        });
        setIsCreating(false);
      } else {
        await axiosInstance.put(
          `${process.env.REACT_APP_BASE_API_URL}notificationtemplates/${updatedTemplate.id}`,
          updatedTemplate,
        );
        toast({
          title: "Template Updated",
          description: "Email template has been successfully updated.",
          variant: "success",
        });
      }
      fetchTemplates();
    } catch (err) {
      console.error("Error saving template:", err);
      toast({
        title: isCreating ? "Create Failed" : "Update Failed",
        description: isCreating
          ? "Failed to create email template. Please try again."
          : "Failed to update email template. Please try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="container mx-auto p-6">
      <h1 className="text-3xl font-bold mb-8">Email Templates</h1>

      <div className="flex justify-between items-center mb-4">
        <CardHeader>
          <CardTitle>Email Templates Configuration</CardTitle>
        </CardHeader>
        <Button onClick={handleCreate}>
          <Plus className="h-4 w-4 mr-2" />
          Create New Template
        </Button>
      </div>

      <Card>
        <CardContent>
          {loading ? (
            <div className="flex justify-center items-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <span className="ml-2">Loading templates...</span>
            </div>
          ) : error ? (
            <div className="text-center py-8 text-destructive">
              <p>{error}</p>
              <Button variant="link" onClick={fetchTemplates} className="mt-4">
                Try Again
              </Button>
            </div>
          ) : (
            <EmailTemplateList templates={templates} onEdit={handleEdit} />
          )}
        </CardContent>
      </Card>

      <EmailTemplateEditDialog
        isOpen={isEditorOpen}
        onOpenChange={(open) => {
          setIsEditorOpen(open);
          if (!open) {
            setIsCreating(false);
            setSelectedTemplate(null);
          }
        }}
        template={selectedTemplate}
        onSave={handleSave}
      />
    </div>
  );
}
